import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService, LoginRequest, LoginResponse, ApiResponse } from '../services/api';

interface User {
  _id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  gstNumber: string;
  panNumber: string;
  panImage: string;
  profileImageUrl: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<ApiResponse<LoginResponse>>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already authenticated on app load
    const token = apiService.getToken();
    if (token) {
      // In a real app, you might want to validate the token with the server
      // For now, we'll assume the token is valid if it exists
      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (error) {
          console.error('Error parsing user data:', error);
          apiService.logout();
        }
      }
    }
    setIsLoading(false);

    // Listen for unauthorized events (401 errors)
    const handleUnauthorized = () => {
      console.warn('Unauthorized event received - logging out user');
      setUser(null);
      setError(null);
      apiService.logout();
      localStorage.removeItem('userData');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const login = async (credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.login(credentials);
      
      console.log('AuthContext - Full response:', response);
      
      if (response.success && response.data) {
        // The response.data contains the actual API response
        const apiResponse = response.data as any;
        console.log('AuthContext - API Response:', apiResponse);
        
        if (apiResponse.success && apiResponse.data && apiResponse.data.vendor) {
          const v = apiResponse.data.vendor;
          const userData: User = {
            _id: v._id,
            email: v.email,
            name: v.name,
            phone: v.phone,
            address: v.address ?? '',
            city: v.city ?? '',
            state: v.state ?? '',
            zip: v.zip ?? '',
            gstNumber: v.gstNumber ?? '',
            panNumber: v.panNumber ?? '',
            panImage: v.panImage ?? '',
            profileImageUrl: v.profileImageUrl ?? '',
            createdAt: v.createdAt ?? '',
            updatedAt: v.updatedAt ?? '',
            __v: v.__v ?? 0,
            role: 'vendor'
          };
          setUser(userData);
          localStorage.setItem('userData', JSON.stringify(userData));
          return response;
        }
        if (apiResponse.success && apiResponse.user) {
          setError('This portal is for vendor accounts only. Please sign in with your vendor email or phone.');
          return response;
        }
        console.log('AuthContext - Invalid response format:', apiResponse);
        setError('Invalid response format from server');
        return response;
      } else {
        console.log('AuthContext - Response not successful:', response);
        setError(response.error || 'Login failed');
        return response;
      }
    } catch (error) {
      const errorMessage = 'An unexpected error occurred during login';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setError(null);
    apiService.logout();
    localStorage.removeItem('userData');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      console.log('AuthContext - Current user:', user);
      console.log('AuthContext - Updating with data:', userData);
      const updatedUser = { ...user, ...userData };
      console.log('AuthContext - Updated user:', updatedUser);
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
